/**
 * MigrationExport.ts (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file MigrationExport.ts
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import { BASE_API_URL } from '../Config';
import { HttpClient } from '@abstract/abstractwebcommon-client/utils/HttpClient';
import { createLog } from './Logs';
import { IAPIEntityResponse } from '@abstract/abstractwebcommon-shared/interfaces/api';
import {
  IMigrationExportSummary,
  IMigrationExportToken
} from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/MigrationExport';

const httpClient = new HttpClient(BASE_API_URL, createLog);

const baseURLPath: string = '/migration/';

/**
 * Read what the export would contain and what still has to be drained before it is taken.
 * @returns Promise<IAPIEntityResponse<IMigrationExportSummary>>.
 */
export const getMigrationExportSummaryService = (): Promise<
  IAPIEntityResponse<IMigrationExportSummary>
> => {
  return httpClient.get(`${baseURLPath}export/summary`);
};

/**
 * Obtain a single-use download ticket for the export.
 * @returns Promise<IAPIEntityResponse<IMigrationExportToken>>.
 */
export const createMigrationExportTokenService = (): Promise<
  IAPIEntityResponse<IMigrationExportToken>
> => {
  return httpClient.post(`${baseURLPath}export/token`, {});
};

/**
 * Where the browser is sent to download the export.
 *
 * **Deliberately not an `HttpClient` call.** That client unconditionally parses a response as JSON
 * and its only download path buffers the body into a Blob, so an export of this size cannot be
 * delivered through it at all — the tab would hold the whole file in memory before writing a byte,
 * assuming it survived the parse. A plain navigation lets the browser stream it to disk, and the
 * one-time ticket in the query string is what stands in for the `Authorization` header a navigation
 * cannot carry.
 * @param token The single-use ticket.
 * @returns The download URL.
 */
export const migrationExportDownloadURL = (token: string): string =>
  `${BASE_API_URL}${baseURLPath}export?token=${encodeURIComponent(token)}`;

/**
 * Where the browser is sent to download the legacy invoice PDFs.
 *
 * A second download rather than a section of the first: the PDFs are two orders of magnitude larger
 * than the records, and re-taking the records must not mean moving the paperwork again. It needs a
 * ticket of its own, because a ticket is spent on first use.
 * @param token The single-use ticket.
 * @returns The download URL.
 */
export const migrationDocumentsDownloadURL = (token: string): string =>
  `${BASE_API_URL}${baseURLPath}export/documents?token=${encodeURIComponent(
    token
  )}`;
